// src/components/Student/StudentPortal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import drIsInLogo from "../../assets/dr-is-in.png";
import caseyImage from "../../assets/Casey_2.png";
import AvatarGalleryModal, { AvatarItem } from "./AvatarGalleryModal";
import VideoRoom from "../Realtime/VideoRoom";
import AvatarImage from "../common/AvatarImage";
import "./StudentPortal.css";
import api from "../../api";
import {
  LANGUAGE_OPTIONS,
  getLanguageCode,
  isRtlLanguage,
} from "../../config/languages";

type DoctorLite = {
  id: number;
  name: string;
  specialties?: string | null;
  medical_field?: string | null;
  is_online?: boolean;
  is_available?: boolean;
  status?: string | null;
  email?: string | null;
  photo_url?: string | null;
};

type InviteMode = "chat" | "video";

type DeviceLocation = {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  captured_at: string;
  permission_status: "granted" | "denied" | "unavailable" | "timeout";
};

async function captureDeviceLocation(): Promise<DeviceLocation> {
  if (!("geolocation" in navigator)) {
    return { captured_at: new Date().toISOString(), permission_status: "unavailable" };
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        captured_at: new Date(position.timestamp || Date.now()).toISOString(),
        permission_status: "granted",
      }),
      (error) => {
        const permission_status: DeviceLocation["permission_status"] =
          error.code === error.PERMISSION_DENIED ? "denied" :
          error.code === error.TIMEOUT ? "timeout" : "unavailable";
        console.warn("AURA ERH geolocation unavailable", permission_status, error.message);
        resolve({ captured_at: new Date().toISOString(), permission_status });
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  });
}

type LiveMsg = {
  id: string;
  from: "student" | "doctor" | "system";
  text: string;
  ts?: number;
  originalText?: string;
  translated?: boolean;
};

/** Response shape from /ai/chat (backend) */
type AiChatResponse = {
  reply: string;
  trigger_level?:
    | "none"
    | "mild_distress"
    | "moderate_distress"
    | "high_risk"
    | "immediate_danger"
    | string;
  escalation?: boolean;
  student_pk?: any;
  session_id?: string;
  analysis?: Record<string, any>;
  classification?: Record<string, any>;
  metadata?: Record<string, any>;
  state?: Record<string, any>;
  conversation_state?: Record<string, any>;
  actions?: string[];
  safety_plan?: string[];
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function clean(v: any) {
  return String(v ?? "").trim();
}

const IMMEDIATE_SAFETY_PATTERNS: RegExp[] = [
  /\b(kill|hurt|harm) myself\b/i,
  /\b(end|take) my life\b/i,
  /\b(don[’']?t|do not|no longer) want to live\b/i,
  /\b(don[’']?t|do not) want to be alive\b/i,
  /\b(suicide|suicidal)\b/i,
  /\b(overdose|hang myself|jump off|shoot myself)\b/i,
  /\bI hate (myself|my life)\b/i,

  // Spanish
  /\bno quiero vivir\b/i,
  /\bya no quiero vivir\b/i,
  /\bno quiero seguir viviendo\b/i,
  /\bquiero morir\b/i,
  /\bme quiero morir\b/i,
  /\bquiero matarme\b/i,
  /\bmatarme\b/i,
  /\bsuicid(?:io|arme|a|al)?\b/i,
  /\bhacerme da(?:ñ|n)o\b/i,
  /\bodio mi vida\b/i,

  // French
  /\bje (?:ne )?veux plus vivre\b/i,
  /\bje veux mourir\b/i,
  /\bje veux me tuer\b/i,
  /\bme suicider\b/i,

  // German
  /\bich will nicht mehr leben\b/i,
  /\bich will sterben\b/i,
  /\bich will mich umbringen\b/i,
  /\bselbstmord\b/i,

  // Dutch
  /\bik wil niet meer leven\b/i,
  /\bik wil dood\b/i,
  /\bik wil mezelf doden\b/i,
  /\bzelfmoord\b/i,

  // Portuguese
  /\bn[aã]o quero (?:mais )?viver\b/i,
  /\bquero morrer\b/i,
  /\bquero me matar\b/i,
  /\bsuic[ií]dio\b/i,

  // Italian
  /\bnon voglio pi[uù] vivere\b/i,
  /\bvoglio morire\b/i,
  /\bvoglio uccidermi\b/i,
  /\bsuicid(?:io|armi)\b/i,

  // Chinese (word boundaries do not work reliably for CJK text)
  /我不想活/,
  /我想死/,
  /我要死/,
  /我想自杀/,
  /自杀/,

  // Japanese
  /死にたい/,
  /もう生きたくない/,
  /自殺したい/,
  /自殺/,

  // Korean
  /죽고 싶/,
  /살고 싶지 않/,
  /자살하고 싶/,
  /자살/,

  // Arabic
  /لا أريد أن أعيش/,
  /أريد أن أموت/,
  /أريد الموت/,
  /أريد أن أقتل نفسي/,
  /انتحار/,

  // Hindi
  /मैं जीना नहीं चाहता/,
  /मैं जीना नहीं चाहती/,
  /मैं मरना चाहता/,
  /मैं मरना चाहती/,
  /मैं खुद को मारना चाहता/,
  /मैं खुद को मारना चाहती/,
  /आत्महत्या/,
];

function isImmediateSafetyMessage(text: string): boolean {
  const value = clean(text);
  return !!value && IMMEDIATE_SAFETY_PATTERNS.some((pattern) => pattern.test(value));
}

function boolFromAny(value: any, defaultValue = false): boolean {
  if (value === null || value === undefined || value === "") return defaultValue;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const text = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "y", "on", "active", "approved", "available", "on call", "on_call"].includes(text)) return true;
  if (["0", "false", "no", "n", "off", "inactive", "unapproved", "not approved", "denied", "rejected"].includes(text)) return false;
  return defaultValue;
}
function normOrgCode(v: string) {
  return clean(v).replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}
function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}
function resolveUrl(url?: string | null) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = API_BASE.replace(/\/+$/, "");
  const slash = url.startsWith("/") ? "" : "/";
  return `${base}${slash}${url}`;
}

const STORES: Storage[] = [sessionStorage, localStorage];

function getFirst(keys: string[]): string {
  for (const store of STORES) {
    for (const k of keys) {
      try {
        const v = store.getItem(k);
        if (v && String(v).trim()) return String(v).trim();
      } catch {}
    }
  }
  return "";
}

function removeBoth(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {}
  try {
    localStorage.removeItem(key);
  } catch {}
}

// ✅ "Back means portal" contract (single source of truth)
const STUDENT_PORTAL_ROUTE = "/student/portal";
const STUDENT_PORTAL_RETURN_KEY = "student_portal_return";

function setPortalReturn(path: string) {
  const v = clean(path) || STUDENT_PORTAL_ROUTE;
  try {
    sessionStorage.setItem(STUDENT_PORTAL_RETURN_KEY, v);
  } catch {}
}

function getPortalReturn(): string {
  try {
    const v = sessionStorage.getItem(STUDENT_PORTAL_RETURN_KEY);
    if (v && v.trim()) return v.trim();
  } catch {}
  return STUDENT_PORTAL_ROUTE;
}

/**
 * realtime.py registers clients from query params:
 *  - role=student&student_pk=12
 */
function buildStudentWsUrl(
  apiBase: string,
  studentPk: string,
  opts?: {
    display_name?: string;
    org_code?: string;
    student_id?: string;
    avatar_id?: string;
    avatar_src?: string;
    preferred_language?: string;
  }
) {
  const base = (apiBase || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
  const wsBase = base.startsWith("https://") ? base.replace(/^https:/, "wss:") : base.replace(/^http:/, "ws:");
  const qs = new URLSearchParams();
  qs.set("role", "student");
  qs.set("student_pk", String(studentPk || "0"));

  if (opts?.display_name) qs.set("display_name", clean(opts.display_name));
  if (opts?.org_code) qs.set("org_code", clean(opts.org_code));
  if (opts?.student_id) qs.set("student_id", clean(opts.student_id));
  if (opts?.avatar_id) qs.set("avatar_id", clean(opts.avatar_id));
  if (opts?.avatar_src) qs.set("avatar_src", clean(opts.avatar_src));
  if (opts?.preferred_language) qs.set("preferred_language", clean(opts.preferred_language));

  return `${wsBase}/realtime/ws?${qs.toString()}`;
}

// Stable room id derived from student_pk + doctor_id
function makeRoomIdFromPk(studentPk: string, doctorId: number | string) {
  const spk = clean(studentPk) || "0";
  const did = clean(String(doctorId)) || "0";
  return `dm:PK:${spk}:${did}`;
}

function makeClientMsgId() {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function makeCaseySessionId(kind: "student", principal: string, orgCode?: string) {
  const p = clean(principal).replace(/[^A-Za-z0-9_-]/g, "") || "anon";
  const o = clean(orgCode).replace(/[^A-Za-z0-9_-]/g, "") || "org";
  return `${kind}:${o}:${p}`;
}

function getStoredCaseySessionId(storageKey: string, fallbackId: string) {
  try {
    const s1 = sessionStorage.getItem(storageKey);
    if (clean(s1)) return clean(s1);
  } catch {}
  try {
    const s2 = localStorage.getItem(storageKey);
    if (clean(s2)) return clean(s2);
  } catch {}
  try {
    sessionStorage.setItem(storageKey, fallbackId);
  } catch {}
  try {
    localStorage.setItem(storageKey, fallbackId);
  } catch {}
  return fallbackId;
}

function persistCaseySessionId(storageKey: string, sessionId: string) {
  const v = clean(sessionId);
  if (!v) return;
  try {
    sessionStorage.setItem(storageKey, v);
  } catch {}
  try {
    localStorage.setItem(storageKey, v);
  } catch {}
}

function getEventType(raw: any): string {
  const t =
    raw?.type ??
    raw?.event ??
    raw?.action ??
    raw?.name ??
    raw?.kind ??
    raw?.payload?.type ??
    raw?.payload?.event ??
    "";
  return String(t || "").toLowerCase();
}

function isInviteAccept(raw: any): boolean {
  const t = getEventType(raw);

  // Standard live-advisor acceptance events.
  if (t.includes("invite") && t.includes("accept")) return true;
  if (t === "accepted") return true;

  // AURA ERH uses the incident workflow rather than the ordinary invite
  // workflow. Treat assignment/acceptance of the primary advisor as the same
  // student-side state transition so the live chat composer opens.
  const isErhEvent =
    t.includes("aura.erh") ||
    t.includes("aura.sos") ||
    t.includes("erh.") ||
    t.includes("incident");

  const isAcceptedState =
    t.includes("accept") ||
    t.includes("assign") ||
    t.includes("attending") ||
    t.includes("primary") ||
    clean(raw?.status).toLowerCase() === "accepted" ||
    clean(raw?.incident?.status).toLowerCase() === "accepted" ||
    clean(raw?.payload?.status).toLowerCase() === "accepted";

  const hasPrimaryAdvisor =
    !!clean(
      raw?.primary_advisor_id ??
      raw?.attending_advisor_id ??
      raw?.doctor_id ??
      raw?.incident?.primary_advisor_id ??
      raw?.incident?.attending_advisor_id ??
      raw?.payload?.primary_advisor_id ??
      raw?.payload?.attending_advisor_id
    );

  return isErhEvent && (isAcceptedState || hasPrimaryAdvisor);
}

function isInviteDecline(raw: any): boolean {
  const t = getEventType(raw);
  if (t.includes("invite") && (t.includes("declin") || t.includes("reject"))) return true;
  return false;
}

function isChatMessage(raw: any): boolean {
  const t = getEventType(raw);
  return t === "chat.message" || t === "chat.msg" || t === "chat.send";
}

function isPresenceSnapshot(raw: any): boolean {
  const t = getEventType(raw);
  return t === "presence.snapshot" || t.includes("presence");
}

/** Helper: call backend AI chat endpoint with full student context */
async function postAiChat(
  message: string,
  opts?: {
    studentId?: string;
    studentPk?: string;
    orgCode?: string;
    displayName?: string;
    sessionId?: string;
    conversationState?: Record<string, any>;
  },
): Promise<AiChatResponse> {
  const msg = clean(message);

  const conversationState =
    opts?.conversationState && typeof opts.conversationState === "object"
      ? opts.conversationState
      : {};

  const res = await api.post("/ai/chat", {
    message: msg,
    role: "student",
    user_role: "student",
    audience: "student",
    speaker_role: "student",
    source: "student_portal",
    session_id: clean(opts?.sessionId),
    student_id: clean(opts?.studentId),
    student_pk: clean(opts?.studentPk),
    org_code: clean(opts?.orgCode),
    display_name: clean(opts?.displayName),
    conversation_state: conversationState,
    context: {
      audience: "student",
      role: "student",
      user_role: "student",
      speaker_role: "student",
      source: "student_portal",
      student_id: clean(opts?.studentId),
      student_pk: clean(opts?.studentPk),
      org_code: clean(opts?.orgCode),
      display_name: clean(opts?.displayName),
      conversation_state: conversationState,
      state: conversationState,
    },
  });

  const data = res?.data ?? {};

  return {
    reply: String(data?.reply ?? ""),
    trigger_level: data?.trigger_level ?? "none",
    escalation: !!data?.escalation,
    student_pk: data?.student_pk,
    session_id: String(data?.session_id ?? clean(opts?.sessionId)),
    analysis:
      typeof data?.analysis === "object" && data?.analysis ? data.analysis : {},
    classification:
      typeof data?.classification === "object" && data?.classification
        ? data.classification
        : {},
    metadata:
      typeof data?.metadata === "object" && data?.metadata ? data.metadata : {},
    state: typeof data?.state === "object" && data?.state ? data.state : {},
    conversation_state:
      typeof data?.state?.conversation_state === "object" &&
      data?.state?.conversation_state
        ? data.state.conversation_state
        : typeof data?.metadata?.conversation_state === "object" &&
            data?.metadata?.conversation_state
          ? data.metadata.conversation_state
          : typeof data?.conversation_state === "object" &&
              data?.conversation_state
            ? data.conversation_state
            : {},
    actions: Array.isArray(data?.actions) ? data.actions : [],
    safety_plan: Array.isArray(data?.safety_plan) ? data.safety_plan : [],
  };
}

function mapDoctorsFromPresence(list: any[]): DoctorLite[] {
  const mapped: DoctorLite[] = (Array.isArray(list) ? list : [])
    .map((d: any) => {
      const meta = d?.meta || {};
      const id = Number(d?.id ?? d?.doctor_id ?? meta?.doctor_id ?? 0);
      if (!Number.isFinite(id) || id <= 0) return null;

      const name = String(d?.name ?? meta?.name ?? meta?.display_name ?? `Advisor ${id}`).trim();
      const specialties = (d?.specialties ?? meta?.specialties ?? meta?.specialty ?? null) as string | null;
      const medical_field = (d?.medical_field ?? meta?.medical_field ?? meta?.field ?? null) as string | null;
      const photo_url_raw = (d?.photo_url ?? meta?.photo_url ?? meta?.photo ?? null) as string | null;
      const email = (d?.email ?? meta?.email ?? null) as string | null;

      return {
        id,
        name: name || `Advisor ${id}`,
        specialties,
        medical_field,
        is_online: d?.is_online != null ? !!d?.is_online : true,
        is_available: d?.is_available != null ? !!d?.is_available : undefined,
        status: (d?.status ?? meta?.status ?? null) as string | null,
        email,
        photo_url: photo_url_raw ? resolveUrl(String(photo_url_raw)) : null,
      } as DoctorLite;
    })
    .filter(Boolean) as DoctorLite[];

  mapped.sort((a, b) => {
    const on = Number(!!b.is_online) - Number(!!a.is_online);
    if (on) return on;
    const av = Number(!!b.is_available) - Number(!!a.is_available);
    if (av) return av;
    return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  });

  return mapped;
}

function mapDoctorsFromDb(list: any[]): DoctorLite[] {
  const mapped: DoctorLite[] = (Array.isArray(list) ? list : [])
    .map((d: any) => {
      const id = Number(d?.id ?? d?.doctor_id ?? d?.doctorId ?? 0);
      if (!Number.isFinite(id) || id <= 0) return null;

      const statusText = String(d?.status ?? d?.approval_status ?? "").trim().toLowerCase();
      const isActive =
        d?.is_active === undefined && d?.active === undefined
          ? statusText !== "inactive" && statusText !== "retired"
          : boolFromAny(d?.is_active ?? d?.active, true);
      if (!isActive) return null;

      // Keep the beta roster consistent: only hide an advisor when the backend
      // explicitly marks the advisor unapproved. Missing approval fields are
      // treated as approved for compatibility with older beta databases.
      const approvedRaw = d?.approved ?? d?.is_approved ?? d?.approval_status ?? d?.status;
      const isApproved =
        approvedRaw === undefined || approvedRaw === null || approvedRaw === ""
          ? true
          : statusText === "inactive" || statusText === "retired"
          ? false
          : boolFromAny(approvedRaw, true);
      if (!isApproved) return null;

      const name = String(d?.name ?? d?.display_name ?? "").trim() || `Advisor ${id}`;
      const photo_url_raw = (d?.photo_url ?? d?.avatar_url ?? null) as string | null;

      return {
        id,
        name,
        specialties: (d?.specialties ?? d?.specialty ?? null) as string | null,
        medical_field: (d?.medical_field ?? d?.profession ?? d?.field ?? null) as string | null,
        status: (d?.status ?? d?.approval_status ?? null) as string | null,
        photo_url: photo_url_raw ? resolveUrl(String(photo_url_raw)) : null,
        is_online: boolFromAny(d?.is_online ?? d?.online, false),
        is_available: boolFromAny(d?.is_available ?? d?.availability ?? d?.available, false),
        email: (d?.email ?? null) as string | null,
      } as DoctorLite;
    })
    .filter(Boolean) as DoctorLite[];

  mapped.sort((a, b) => {
    const on = Number(!!b.is_online) - Number(!!a.is_online);
    if (on) return on;
    const av = Number(!!b.is_available) - Number(!!a.is_available);
    if (av) return av;
    return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  });

  return mapped;
}

function mergeDoctorLists(roster: DoctorLite[], presence: DoctorLite[]): DoctorLite[] {
  const byId = new Map<number, DoctorLite>();
  for (const d of roster || []) byId.set(Number(d.id), d);

  for (const p of presence || []) {
    const id = Number(p.id);
    if (!id) continue;
    const cur = byId.get(id);
    byId.set(id, { ...(cur || ({} as any)), ...(p || ({} as any)), id });
  }

  const out = Array.from(byId.values());
  out.sort((a, b) => {
    const on = Number(!!b.is_online) - Number(!!a.is_online);
    if (on) return on;
    const av = Number(!!b.is_available) - Number(!!a.is_available);
    if (av) return av;
    return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
  });

  return out;
}

function AvatarCircle({ src, fallback, alt }: { src?: string; fallback: string; alt: string }) {
  return (
    <AvatarImage
      src={src}
      alt={alt}
      size={34}
      fit="cover"
      radius={12}
      background="#f3f6f9"
      border="1px solid #e8eef5"
      fallback={<span style={{ fontWeight: 950, opacity: 0.85 }}>{fallback}</span>}
    />
  );
}

// ✅ Doctor Profile popup modal (student-side)
function DoctorProfileModal({
  open,
  doctor,
  onClose,
}: {
  open: boolean;
  doctor: DoctorLite | null;
  onClose: () => void;
}) {
  if (!open || !doctor) return null;

  const field = doctor.medical_field || doctor.specialties || "General Medicine";
  const online = doctor.is_online ? "Online" : "Offline";
  const avail = doctor.is_available ? " • Available" : "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        overflowY: "auto",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(760px, 96vw)",
          marginTop: 24,
          background: "#fff",
          borderRadius: 14,
          padding: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 14,
                background: "rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.08)",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {doctor.photo_url ? (
                <img
                  src={doctor.photo_url}
                  alt={doctor.name || `Advisor #${doctor.id}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <span style={{ fontWeight: 900, opacity: 0.7 }}>
                  {(doctor.name || "D").slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>

            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{doctor.name || `Advisor #${doctor.id}`}</div>
              <div style={{ marginTop: 2, opacity: 0.8, fontWeight: 800 }}>
                {field} • {online}
                {avail}
              </div>
              {doctor.status ? <div style={{ marginTop: 6, opacity: 0.85 }}>{doctor.status}</div> : null}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "1px solid rgba(0,0,0,0.15)",
              background: "#fff",
              borderRadius: 10,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 900,
              height: 36,
            }}
          >
            ✕
          </button>
        </div>

        {doctor.email ? (
          <div style={{ marginTop: 12, opacity: 0.82 }}>
            <span style={{ fontWeight: 900 }}>Email:</span> {doctor.email}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function StudentPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();

  // ✅ Make "portal return" deterministic:
  useEffect(() => {
    const here = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    setPortalReturn(here || STUDENT_PORTAL_ROUTE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash]);

  // token is mostly for fetch() fallback; axios api.ts handles auth itself
  const token = useMemo(
    () => normalizeBearer(getFirst(["student_access_token", "student_token", "access_token", "token"])),
    []
  );

  const studentId = useMemo(() => getFirst(["student_id"]) || "", []);
  const studentPk = useMemo(() => getFirst(["student_pk"]) || "", []);
  const orgCodeRaw = useMemo(() => getFirst(["org_code"]) || "", []);
  const orgCode = normOrgCode(orgCodeRaw);

  // Fallback avatars (static)
  const PUBLIC = (process.env.PUBLIC_URL || "").trim();
  const AVATARS: AvatarItem[] = useMemo(() => {
    const items: AvatarItem[] = [];
    for (let i = 1; i <= 17; i++) {
      const n = String(i).padStart(2, "0");
      items.push({
        id: `avatar_${n}`,
        label: `Avatar ${n}`,
        src: `${PUBLIC}/avatar/avatar_${n}.png`,
        tags: ["student"],
      });
    }
    return items;
  }, [PUBLIC]);

  // -------------------------
  // Profile
  // -------------------------
  const [displayName, setDisplayName] = useState("");
  const [aboutMe, setAboutMe] = useState("");
  const [themePreference, setThemePreference] = useState<"Light" | "Calm">("Light");
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [avatarId, setAvatarId] = useState<string>("");
  const [avatarSrc, setAvatarSrc] = useState<string>("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [profileNotice, setProfileNotice] = useState("");
  const customPhotoInputRef = useRef<HTMLInputElement | null>(null);

  // Doctor profile popup state
  const [doctorProfileOpen, setDoctorProfileOpen] = useState(false);
  const [doctorProfileDoc, setDoctorProfileDoc] = useState<DoctorLite | null>(null);
  const userTriggeredDoctorSelectRef = useRef(false);

  // -------------------------
  // Tabs + AI chat
  // -------------------------
  const [tab, setTab] = useState<"ai" | "live">("ai");

  const [aiText, setAiText] = useState("");
  const [aiMessages, setAiMessages] = useState<
    Array<{ id: string; from: "student" | "system"; text: string; ts: number }>
  >([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [translateLanguage, setTranslateLanguage] = useState("English");
  const translateLanguageRef = useRef<string>("English");
  const [systemNotice, setSystemNotice] = useState<string>("");
  const [aiConversationState, setAiConversationState] = useState<Record<string, any>>({});
  const [lastCaseyContext, setLastCaseyContext] = useState<Record<string, any>>({});

  const greetingName = clean(displayName) || clean(studentId) || "Student";
  const studentFallback = (clean(displayName) || clean(studentId) || "S").slice(0, 1).toUpperCase();

  const caseySessionStorageKey = useMemo(
    () => `casey_session:student:${orgCode}:${clean(studentPk) || clean(studentId) || "anon"}`,
    [orgCode, studentPk, studentId],
  );

  const [caseySessionId, setCaseySessionId] = useState(() =>
    getStoredCaseySessionId(
      `casey_session:student:${normOrgCode(orgCodeRaw)}:${clean(studentPk) || clean(studentId) || "anon"}`,
      makeCaseySessionId(
        "student",
        clean(studentPk) || clean(studentId) || "anon",
        normOrgCode(orgCodeRaw),
      ),
    ),
  );

  useEffect(() => {
    const fallback = makeCaseySessionId(
      "student",
      clean(studentPk) || clean(studentId) || "anon",
      orgCode,
    );
    const stable = getStoredCaseySessionId(caseySessionStorageKey, fallback);
    setCaseySessionId(stable);
  }, [caseySessionStorageKey, orgCode, studentPk, studentId]);

  const createFreshCaseySessionId = () => {
    const principal = clean(studentPk) || clean(studentId) || "anon";
    const base = makeCaseySessionId("student", principal, orgCode);
    return `${base}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
  };

  const resetCaseyConversation = (clearMessages = true) => {
    const freshSessionId = createFreshCaseySessionId();
    setCaseySessionId(freshSessionId);
    persistCaseySessionId(caseySessionStorageKey, freshSessionId);
    setAiConversationState({});
    setSystemNotice("");
    setAiText("");
    setAiBusy(false);
    if (clearMessages) {
      setAiMessages([]);
    }
  };

  useEffect(() => {
    translateLanguageRef.current = translateLanguage;
  }, [translateLanguage]);

  const handleLanguageChange = (nextLanguage: string) => {
    const cleanLanguage = clean(nextLanguage) || "English";
    if (cleanLanguage === translateLanguage) return;
    translateLanguageRef.current = cleanLanguage;
    setTranslateLanguage(cleanLanguage);
    resetCaseyConversation(true);
  };

  const handleNewCaseyChat = () => {
    resetCaseyConversation(true);
  };

  // ✅ Scroll refs (AI + LIVE)
  const aiScrollBoxRef = useRef<HTMLDivElement | null>(null);
  const aiEndRef = useRef<HTMLDivElement | null>(null);
  const liveScrollBoxRef = useRef<HTMLDivElement | null>(null);
  const liveEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = (box: HTMLDivElement | null, end: HTMLDivElement | null, smooth = true) => {
    try {
      if (end?.scrollIntoView) end.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });
      if (box) box.scrollTop = box.scrollHeight;
    } catch {}
  };

  // ✅ Auto-scroll AI thread when tab is active and messages grow
  useEffect(() => {
    if (tab !== "ai") return;
    const raf = window.requestAnimationFrame(() => {
      scrollToBottom(aiScrollBoxRef.current, aiEndRef.current, true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [tab, aiMessages.length]);

  // Persist display name into both stores (legacy key)
  useEffect(() => {
    try {
      localStorage.setItem("display_name", clean(displayName));
      sessionStorage.setItem("display_name", clean(displayName));
    } catch {}
  }, [displayName]);

  const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const translateText = async (text: string, target: string): Promise<string> => {
    if (!text) return text;

    try {
      const res = await api.post("/translate", {
        text,
        target: getLanguageCode(target),
        source: "auto",
      });

      return res?.data?.translated_text || res?.data?.translation || res?.data?.text || text;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Translation error:", err);
      return text;
    }
  };

  const typeCaseyReply = async (reply: string, typingId: string) => {
    setAiMessages((prev) =>
      prev.map((m) => (m.id === typingId ? { ...m, text: "" } : m)),
    );

    for (let i = 0; i < reply.length; i++) {
      const char = reply[i];
      const nextText = reply.slice(0, i + 1);

      setAiMessages((prev) =>
        prev.map((m) => (m.id === typingId ? { ...m, text: nextText } : m)),
      );

      let delay = 12;
      if ([".", "!", "?"].includes(char)) delay += 45;
      if ([",", ";", ":"].includes(char)) delay += 22;

      await sleep(delay);
    }
  };

  const sendAi = async () => {
    const text = clean(aiText);
    if (!text || aiBusy) return;

    const typingId = `casey_typing_${Date.now()}_${Math.random()
      .toString(16)
      .slice(2)}`;

    setAiMessages((prev) => [
      ...prev,
      { id: makeClientMsgId(), from: "student", text, ts: Date.now() },
      {
        id: typingId,
        from: "system",
        text: "Casey is typing...",
        ts: Date.now(),
      },
    ]);

    setAiText("");
    setAiBusy(true);
    setSystemNotice("");

    try {
      let outgoingText = text;
      if (translateLanguage !== "English") {
        outgoingText = await translateText(text, "English");
      }

      const localImmediateSafety =
        isImmediateSafetyMessage(text) || isImmediateSafetyMessage(outgoingText);

      // Start ERH immediately on unmistakable crisis language instead of
      // waiting for Casey's classification/response round trip.
      if (localImmediateSafety) {
        void handleAuraSos("automatic", {
          triggerLevel: "local_immediate_safety_match",
          category: "self_harm_suicide_risk",
          originatingMessage: text,
          forceNew: false,
        });
      }

      const data = await postAiChat(outgoingText, {
        studentId,
        studentPk,
        orgCode,
        displayName: greetingName,
        sessionId: caseySessionId,
        conversationState: aiConversationState,
      });

      const caseyClassification = data.classification && typeof data.classification === "object" ? data.classification : {};
      const caseyMetadata = data.metadata && typeof data.metadata === "object" ? data.metadata : {};
      setLastCaseyContext({
        primary_category: clean(caseyClassification?.primary_category || caseyMetadata?.active_incident || caseyMetadata?.category),
        risk_level: clean(caseyClassification?.risk_level || caseyMetadata?.risk_level || data.trigger_level),
        trigger_level: clean(data.trigger_level),
        escalation: !!data.escalation,
        updated_at: new Date().toISOString(),
      });

      const returnedSessionId = clean(data.session_id);
      if (returnedSessionId && returnedSessionId !== caseySessionId) {
        setCaseySessionId(returnedSessionId);
        persistCaseySessionId(caseySessionStorageKey, returnedSessionId);
      } else if (caseySessionId) {
        persistCaseySessionId(caseySessionStorageKey, caseySessionId);
      }

      const nextConversationState =
        data.conversation_state && typeof data.conversation_state === "object"
          ? data.conversation_state
          : data.state?.conversation_state &&
              typeof data.state.conversation_state === "object"
            ? data.state.conversation_state
            : data.metadata?.conversation_state &&
                typeof data.metadata.conversation_state === "object"
              ? data.metadata.conversation_state
              : {};

      if (Object.keys(nextConversationState).length > 0) {
        setAiConversationState(nextConversationState);
      }

      const baseReply =
        clean(data.reply) || "I’m here with you. Can you tell me a little more?";

      let translatedReply = baseReply;
      if (translateLanguage !== "English") {
        translatedReply = await translateText(baseReply, translateLanguage);
      }

      const lastSystem = aiMessages
        .slice()
        .reverse()
        .find((m) => m.from === "system" && m.id !== typingId);

      const isRepeat = !!(
        lastSystem && clean(lastSystem.text) === clean(translatedReply)
      );

      const crisisReplyEnglish =
        "I’m here with you. What you just said is serious. I’m connecting you with a live advisor now. If you are in immediate danger, call 911 or 988 in the U.S., or tell a trusted adult right now.";

      const crisisReply =
        localImmediateSafety && translateLanguage !== "English"
          ? await translateText(crisisReplyEnglish, translateLanguage)
          : crisisReplyEnglish;

      const finalReply = localImmediateSafety
        ? crisisReply
        : isRepeat
          ? translateLanguage !== "English"
            ? translatedReply
            : baseReply +
              " If you want, tell me one detail: where is this happening (school, home, online), and what’s the hardest moment?"
          : translatedReply;

      await typeCaseyReply(finalReply, typingId);

      const trigger = String(data.trigger_level ?? "none").toLowerCase();
      const escalation = !!data.escalation;

      const urgentTriggers = [
        "immediate_danger",
        "high_risk",
        "sexual_abuse",
        "physical_safety",
        "medical_urgent",
        "basic_needs",
        "unsafe_home",
        "abuse_physical",
      ];

      const automaticSafetyTrigger =
        escalation ||
        urgentTriggers.includes(trigger) ||
        localImmediateSafety;

      if (automaticSafetyTrigger) {
        // Do not rely on a single backend classification field. The local
        // crisis-language guard guarantees that unmistakable self-harm language
        // starts the same ERH workflow as the physical SOS button.
        if (!localImmediateSafety) {
          void handleAuraSos("automatic", {
            triggerLevel: trigger || "backend_safety_trigger",
            category: clean(caseyClassification?.primary_category || caseyMetadata?.active_incident) || "self_harm_suicide_risk",
            originatingMessage: outgoingText,
            forceNew: false,
          });
        }
        if (
          trigger === "immediate_danger" &&
          /shoot up|kill them|kill everyone|hurt them/i.test(outgoingText)
        ) {
          setSystemNotice(
            "If you feel like you might hurt someone, get help immediately: call 911 (U.S.) or tell a trusted adult right now (teacher/counselor/parent/guardian).",
          );
        } else if (trigger === "high_risk" || trigger === "immediate_danger") {
          setSystemNotice(
            "If you feel unsafe or in immediate danger, call or text 988 (U.S.) or reach a trusted adult right now. You can also open Live Advisor and request a human advisor from this portal.",
          );
        } else {
          setSystemNotice(
            "If you are in immediate danger, call 911 (U.S.) now. If you’re at school, tell a counselor/teacher/nurse right away. You deserve to be safe.",
          );
        }
      } else if (trigger === "grief_loss") {
        setSystemNotice(
          "If you have a trusted adult you can talk to (family, counselor, teacher), it can help to share what you’re carrying.",
        );
      } else {
        setSystemNotice("");
      }

      window.requestAnimationFrame(() =>
        scrollToBottom(aiScrollBoxRef.current, aiEndRef.current, true),
      );
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("AI chat error:", err?.response?.status, err?.response?.data || err);

      const fallback =
        translateLanguage !== "English"
          ? await translateText(
              "AI is unavailable right now. Please try again in a moment.",
              translateLanguage,
            )
          : "AI is unavailable right now. Please try again in a moment.";

      await typeCaseyReply(fallback, typingId);
    } finally {
      setAiBusy(false);
    }
  };

  // -------------------------
  // Live doctor WS + invite + chat
  // -------------------------
  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const presencePollRef = useRef<number | null>(null);

  const rosterRef = useRef<DoctorLite[]>([]);

  const [wsStatus, setWsStatus] = useState<"disconnected" | "connecting" | "connected">("disconnected");
  const [wsError, setWsError] = useState<string>("");

  const [onlineDoctors, setOnlineDoctors] = useState<DoctorLite[]>([]);
  const [doctorSource, setDoctorSource] = useState<string>("");

  const [selectedDoctorId, setSelectedDoctorId] = useState<string>(() => {
    const k = `student_chat_doctor:${normOrgCode(orgCodeRaw)}:${clean(studentId)}`;
    return localStorage.getItem(k) || sessionStorage.getItem(k) || "";
  });
  const [typedDoctorId, setTypedDoctorId] = useState<string>("");

  const [liveMode, setLiveMode] = useState<InviteMode>("chat");
  const [liveStatus, setLiveStatus] = useState<"idle" | "requested" | "accepted">("idle");

  const [videoOpen, setVideoOpen] = useState(false);
  const requestedModeRef = useRef<InviteMode>("chat");
  const [roomId, setRoomId] = useState<string>("");

  const [liveMsgs, setLiveMsgs] = useState<LiveMsg[]>([]);
  const [liveText, setLiveText] = useState("");
  const [sosBusy, setSosBusy] = useState(false);
  const [sosStatus, setSosStatus] = useState("");
  const sosBusyRef = useRef(false);
  const activeSosIncidentRef = useRef("");

  // ✅ Auto-scroll LIVE thread
  useEffect(() => {
    if (tab !== "live") return;
    const raf = window.requestAnimationFrame(() => {
      scrollToBottom(liveScrollBoxRef.current, liveEndRef.current, true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [tab, liveMsgs.length, liveStatus]);

  // Robust WS lifecycle state
  const wsRunRef = useRef<boolean>(false);
  const wsAttemptRef = useRef<number>(0);
  const wsConnectSeqRef = useRef<number>(0);
  const reconnectTimerRef = useRef<number | null>(null);

  const stopReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const stopPing = () => {
    if (pingTimerRef.current) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  };

  const startPing = () => {
    stopPing();
    pingTimerRef.current = window.setInterval(() => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      } catch {}
    }, 20000);
  };

  const stopPresencePoll = () => {
    if (presencePollRef.current) {
      window.clearInterval(presencePollRef.current);
      presencePollRef.current = null;
    }
  };

  async function fetchJson(url: string) {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(url, { headers, credentials: "include" });
    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = text;
    }
    return { ok: res.ok, status: res.status, data };
  }

  const refreshRosterFromDb = async () => {
    try {
      // Prefer the authenticated student roster. Fall back to the public roster
      // so older local beta backends still work during testing.
      let result = await fetchJson(`${API_BASE}/student/doctors`);
      if (!result.ok && (result.status === 401 || result.status === 403 || result.status === 404)) {
        result = await fetchJson(`${API_BASE}/public/doctors`);
        if (!result.ok && (result.status === 401 || result.status === 403 || result.status === 404)) {
          result = await fetchJson(`${API_BASE}/public/advisors`);
        }
      }

      const { ok, status, data } = result;
      if (!ok) {
        setDoctorSource(`Advisor roster failed (${status})`);
        return;
      }

      const rawRoster = data?.doctors ?? data?.items ?? data?.advisors ?? data?.results ?? [];
      const roster = mapDoctorsFromDb(rawRoster);
      rosterRef.current = roster;

      setOnlineDoctors((prev) => mergeDoctorLists(roster, prev));
      setDoctorSource(`Advisor roster ✅ (${roster.length})`);
      if (!clean(selectedDoctorId) && roster.length) setSelectedDoctorId(String(roster[0].id));
    } catch {
      setDoctorSource("Advisor roster error");
    }
  };

  const refreshDoctorsFromPresence = async () => {
    try {
      if (!rosterRef.current.length) {
        await refreshRosterFromDb();
      }

      const { ok, status, data } = await fetchJson(`${API_BASE}/realtime/presence`);
      if (!ok) {
        if (rosterRef.current.length) {
          setOnlineDoctors((prev) => mergeDoctorLists(rosterRef.current, prev));
          setDoctorSource(`Advisor roster ✅ (presence failed ${status})`);
        } else {
          setDoctorSource(`Presence failed (${status})`);
        }
        return;
      }

      const doctorsRaw = data?.online?.doctors ?? data?.doctors ?? [];
      const presence = mapDoctorsFromPresence(doctorsRaw);
      const merged = mergeDoctorLists(rosterRef.current, presence);

      setOnlineDoctors(merged);
      setDoctorSource("Advisor roster + presence ✅");
      if (!clean(selectedDoctorId) && merged.length) setSelectedDoctorId(String(merged[0].id));
    } catch {
      if (rosterRef.current.length) {
        setOnlineDoctors((prev) => mergeDoctorLists(rosterRef.current, prev));
        setDoctorSource("Advisor roster ✅ (presence error)");
      } else {
        setDoctorSource("Failed to load advisors.");
      }
    }
  };

  const scheduleReconnect = (why: string) => {
    if (!wsRunRef.current) return;
    if (!clean(studentPk)) return;

    stopReconnectTimer();
    wsAttemptRef.current = Math.min(wsAttemptRef.current + 1, 8);
    const ms = Math.min(1500 * wsAttemptRef.current, 10000);

    setWsError((prev) => {
      const extra = `${why}\nReconnecting in ${Math.round(ms / 100) / 10}s…`;
      return prev && prev.includes("Reconnecting") ? prev : extra;
    });

    reconnectTimerRef.current = window.setTimeout(() => {
      if (!wsRunRef.current) return;
      ensureWs("reconnect");
    }, ms);
  };

  const ensureWs = (_reason: string = "ensure") => {
    if (!wsRunRef.current) return false;

    const cur = wsRef.current;
    if (cur && (cur.readyState === WebSocket.OPEN || cur.readyState === WebSocket.CONNECTING)) {
      return true;
    }

    if (!clean(studentPk)) {
      setWsError("Missing student_pk (database id). Log in again so we can store it.");
      return false;
    }

    stopReconnectTimer();
    stopPing();

    setWsStatus("connecting");

    try {
      if (cur) {
        try {
          cur.onopen = null;
          cur.onclose = null;
          cur.onerror = null;
          cur.onmessage = null;
        } catch {}
        try {
          cur.close();
        } catch {}
      }
    } catch {}

    const displayForDoctor = clean(displayName) || clean(studentId) || "Student";
    const url = buildStudentWsUrl(API_BASE, studentPk, {
      display_name: displayForDoctor,
      org_code: orgCode,
      student_id: clean(studentId),
      avatar_id: clean(avatarId),
      avatar_src: clean(avatarSrc),
      preferred_language: clean(translateLanguageRef.current) || "English",
    });

    const seq = ++wsConnectSeqRef.current;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (seq !== wsConnectSeqRef.current) return;
      wsAttemptRef.current = 0;
      setWsStatus("connected");
      setWsError("");
      startPing();

      try {
        ws.send(
          JSON.stringify({
            type: "presence.set",
            meta: {
              display_name: displayForDoctor,
              org_code: orgCode,
              student_id: clean(studentId),
              avatar_id: clean(avatarId),
              avatar_src: clean(avatarSrc),
              preferred_language: clean(translateLanguageRef.current) || "English",
            },
          })
        );
        ws.send(JSON.stringify({ type: "presence.get" }));
      } catch {}

      void refreshRosterFromDb();
      void refreshDoctorsFromPresence();
    };

    ws.onclose = (ev: CloseEvent) => {
      if (seq !== wsConnectSeqRef.current) return;

      stopPing();
      stopPresencePoll();
      setWsStatus("disconnected");
      setLiveStatus("idle");
      setVideoOpen(false);

      const code = ev?.code;
      const reasonText = (ev?.reason || "").trim();
      const wasClean = ev?.wasClean;

      const diag = `WS closed (code ${code}${wasClean ? ", clean" : ""}${reasonText ? `, reason: ${reasonText}` : ""})`;
      setWsError(diag);

      if (wsRunRef.current) scheduleReconnect(diag);
    };

    ws.onerror = () => {
      if (seq !== wsConnectSeqRef.current) return;
      setWsError("WebSocket error (waiting for close reason/code)...");
    };

    ws.onmessage = (ev) => {
      if (seq !== wsConnectSeqRef.current) return;
      try {
        const data = JSON.parse(ev.data);

        if (isPresenceSnapshot(data)) {
          const doctorsRaw = data?.online?.doctors ?? data?.doctors ?? [];
          const presence = mapDoctorsFromPresence(doctorsRaw);

          if (!rosterRef.current.length) {
            void refreshRosterFromDb();
          }

          const merged = mergeDoctorLists(rosterRef.current, presence);
          setOnlineDoctors((prev) => (merged.length ? mergeDoctorLists(merged, prev) : prev));
          setDoctorSource(`Realtime presence + advisor roster ✅ (${Math.max(merged.length, rosterRef.current.length)})`);
          if (!clean(selectedDoctorId) && merged.length) setSelectedDoctorId(String(merged[0].id));
          return;
        }

        if (getEventType(data) === "aura.erh.location.refresh") {
          const iid = clean(data?.incident_id || activeSosIncidentRef.current);
          if (iid) {
            void captureDeviceLocation().then((fresh) => {
              try {
                if (wsRef.current?.readyState !== WebSocket.OPEN) return;
                wsRef.current.send(JSON.stringify({
                  type: "aura.erh.location.update",
                  incident_id: iid,
                  latitude: fresh.latitude ?? null,
                  longitude: fresh.longitude ?? null,
                  location_accuracy: fresh.accuracy ?? null,
                  location_captured_at: fresh.captured_at,
                  location_permission: fresh.permission_status,
                }));
              } catch {}
            });
          }
          return;
        }

        const eventType = getEventType(data);
        const isErhStudentChat =
          eventType.includes("erh") &&
          (eventType.includes("chat") || eventType.includes("message"));

        if (isChatMessage(data) || isErhStudentChat) {
          const text = String(
            data?.text ??
            data?.message ??
            data?.payload?.text ??
            data?.payload?.message ??
            ""
          );

          const fromRaw = String(
            data?.from ??
            data?.sender ??
            data?.sender_role ??
            data?.role ??
            data?.payload?.from ??
            data?.payload?.sender ??
            data?.payload?.sender_role ??
            "system"
          ).toLowerCase();

          const from: LiveMsg["from"] =
            fromRaw.includes("doctor") || fromRaw.includes("advisor")
              ? "doctor"
              : fromRaw.includes("student")
                ? "student"
                : "system";

          // ERH messages can carry the canonical student room even when the
          // acceptance event arrived under a different event name.
          const messageRoom = clean(
            data?.room_id ??
            data?.student_chat_room_id ??
            data?.incident?.student_chat_room_id ??
            data?.payload?.room_id ??
            data?.payload?.student_chat_room_id
          );
          if (messageRoom) setRoomId(messageRoom);

          if (isErhStudentChat && liveStatus !== "accepted") {
            setLiveStatus("accepted");
            setTab("live");
          }

          if (text) {
            const messageId = String(data?.id || data?.client_msg_id || makeClientMsgId());

            // Keep system/student traffic unchanged. Advisor replies are
            // translated into the student's selected portal language for display.
            const activeLiveLanguage = clean(translateLanguageRef.current) || "English";
            if (from === "doctor" && activeLiveLanguage !== "English") {
              void translateText(text, activeLiveLanguage).then((translatedText) => {
                setLiveMsgs((prev) => [
                  ...prev,
                  {
                    id: messageId,
                    from,
                    text: translatedText || text,
                    originalText: translatedText && translatedText !== text ? text : undefined,
                    translated: !!translatedText && translatedText !== text,
                    ts: Date.now(),
                  },
                ]);
              });
            } else {
              setLiveMsgs((prev) => [
                ...prev,
                { id: messageId, from, text, ts: Date.now() },
              ]);
            }
          }
          return;
        }

        if (getEventType(data) === "invite.sent" || getEventType(data) === "aura.sos.acknowledged") {
          const incidentId = clean(data?.incident_id || data?.incident?.incident_id || data?.payload?.incident_id);
          const canonicalRoom = clean(data?.room_id || data?.incident?.student_chat_room_id || data?.payload?.room_id);
          if (incidentId) activeSosIncidentRef.current = incidentId;
          if (canonicalRoom) {
            setRoomId(canonicalRoom);
            try {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "chat.join", room_id: canonicalRoom }));
              }
            } catch {}
          }
        }

        if (isInviteAccept(data)) {
          setWsError("");
          setLiveStatus("accepted");

          const rid = String(
            data?.room_id ||
            data?.room ||
            data?.student_chat_room_id ||
            data?.incident?.student_chat_room_id ||
            data?.payload?.room_id ||
            data?.payload?.student_chat_room_id ||
            roomId ||
            ""
          );
          if (rid) setRoomId(rid);

          const acceptedIncidentId = clean(
            data?.incident_id ||
            data?.incident?.incident_id ||
            data?.payload?.incident_id
          );
          if (acceptedIncidentId) activeSosIncidentRef.current = acceptedIncidentId;

          // ERH acceptance must visibly move the student out of the waiting
          // state even when the backend event is not named invite.accepted.
          setTab("live");
          setSosStatus("AURA ERH™ advisor connected. You can talk privately with your advisor below.");

          setLiveMsgs((prev) => {
            const acceptedText = "Advisor accepted ✅ — you can type below.";

            // ERH can rebroadcast assignment/acceptance state while the
            // incident remains active. Keep that state update, but show the
            // acceptance notice only once in the student's transcript.
            const alreadyShown = prev.some(
              (m) => m.from === "system" && m.text === acceptedText
            );
            if (alreadyShown) return prev;

            const msg: LiveMsg = {
              id: makeClientMsgId(),
              from: "system",
              text: acceptedText,
              ts: Date.now(),
            };

            if (!prev.length) return [msg];

            const last = prev[prev.length - 1];
            const lastIsWaiting =
              last.from === "system" &&
              last.text.toLowerCase().includes("waiting");

            if (lastIsWaiting) return [...prev.slice(0, -1), msg];
            return [...prev, msg];
          });

          try {
            if (rid && wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "chat.join", room_id: rid }));
            }
          } catch {}

          if (requestedModeRef.current === "video") setVideoOpen(true);
          return;
        }

        if (isInviteDecline(data)) {
          setLiveStatus("idle");
          setVideoOpen(false);
          setWsError("Advisor declined the request.");
          return;
        }
      } catch {}
    };

    return true;
  };

  const disconnectWs = () => {
    wsRunRef.current = false;
    stopReconnectTimer();
    stopPing();
    stopPresencePoll();

    const cur = wsRef.current;
    wsRef.current = null;

    try {
      if (cur) {
        cur.onopen = null;
        cur.onclose = null;
        cur.onerror = null;
        cur.onmessage = null;
        cur.close();
      }
    } catch {}

    setWsStatus("disconnected");
    setWsError("");
    setLiveStatus("idle");
    setVideoOpen(false);
    setRoomId("");
    setLiveMsgs([]);
    setLiveText("");
  };

  const sendLiveChat = async () => {
    const text = liveText.trim();
    if (!text) return;

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setWsError("Realtime not connected.");
      return;
    }
    if (!roomId) {
      setWsError("No room id. Request chat first.");
      return;
    }

    const msgId = makeClientMsgId();

    // The student always sees exactly what they typed. For non-English
    // sessions, only the wire copy is translated to English for the advisor.
    setLiveMsgs((prev) => [...prev, { id: msgId, from: "student", text, ts: Date.now() }]);
    setLiveText("");

    const outgoingText =
      translateLanguage !== "English"
        ? await translateText(text, "English")
        : text;

    try {
      wsRef.current.send(
        JSON.stringify({
          type: "chat.message",
          room_id: roomId,
          text: outgoingText || text,
          client_msg_id: msgId,
        })
      );
    } catch {
      setWsError("Failed to send message (socket closed).");
    }
  };

  // Beta presence fix:
  // Keep the realtime socket open as soon as the Student Portal loads so AURA can
  // count active student usage even when the student never requests a live advisor.
  // The advisor request/chat flow still uses this same socket when the Live tab is opened.
  useEffect(() => {
    if (!clean(studentPk)) return;

    wsRunRef.current = true;

    void refreshRosterFromDb();
    ensureWs("portal-presence");

    stopPresencePoll();
    presencePollRef.current = window.setInterval(() => {
      void refreshDoctorsFromPresence();
    }, 6000);

    return () => {
      disconnectWs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentPk]);

  // Push name/avatar updates into presence meta (best-effort)
  useEffect(() => {
    const displayForDoctor = clean(displayName) || clean(studentId) || "Student";
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: "presence.update",
            meta: {
              display_name: displayForDoctor,
              org_code: orgCode,
              student_id: clean(studentId),
              avatar_id: clean(avatarId),
              avatar_src: clean(avatarSrc),
              preferred_language: clean(translateLanguageRef.current) || "English",
            },
          })
        );
      }
    } catch {}
  }, [displayName, avatarId, avatarSrc, studentId, orgCode, translateLanguage]);

  // Load saved profile for this student/org
  useEffect(() => {
    const key = `student_profile:${orgCode}:${clean(studentId)}`;
    const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.displayName === "string") setDisplayName(parsed.displayName);
      if (typeof parsed?.aboutMe === "string") setAboutMe(parsed.aboutMe);
      if (parsed?.themePreference === "Light" || parsed?.themePreference === "Calm") setThemePreference(parsed.themePreference);
      if (typeof parsed?.remindersEnabled === "boolean") setRemindersEnabled(parsed.remindersEnabled);
      if (typeof parsed?.soundEnabled === "boolean") setSoundEnabled(parsed.soundEnabled);
      if (typeof parsed?.avatarId === "string") setAvatarId(parsed.avatarId);
      if (typeof parsed?.avatarSrc === "string") setAvatarSrc(parsed.avatarSrc);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist profile for this student/org
  useEffect(() => {
    const key = `student_profile:${orgCode}:${clean(studentId)}`;
    const payload = {
      displayName: clean(displayName),
      aboutMe: clean(aboutMe).slice(0, 120),
      themePreference,
      remindersEnabled,
      soundEnabled,
      avatarId: avatarId || "",
      avatarSrc: avatarSrc || "",
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {}
  }, [displayName, aboutMe, themePreference, remindersEnabled, soundEnabled, avatarId, avatarSrc, orgCode, studentId]);

  const clearAvatar = () => {
    setAvatarId("");
    setAvatarSrc("");
    setProfileNotice("Profile image removed.");
  };

  const onPickAvatar = (avatar: AvatarItem) => {
    const id = String((avatar as any)?.id || (avatar as any)?.filename || "");
    setAvatarId(id);
    setAvatarSrc(String((avatar as any)?.src || ""));
    setProfileNotice("Avatar selected and saved on this device.");
    setGalleryOpen(false);
  };

  const handleCustomPhotoUpload = async (file?: File | null) => {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setProfileNotice("Please choose a JPG, PNG, or WebP image.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setProfileNotice("Please choose an image smaller than 5 MB.");
      return;
    }

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Unable to read image"));
        reader.readAsDataURL(file);
      });

      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Unable to load image"));
        img.src = dataUrl;
      });

      const size = 320;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Image processing is unavailable");

      const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
      const sx = Math.max(0, (image.naturalWidth - sourceSize) / 2);
      const sy = Math.max(0, (image.naturalHeight - sourceSize) / 2);
      ctx.drawImage(image, sx, sy, sourceSize, sourceSize, 0, 0, size, size);

      const optimized = canvas.toDataURL("image/jpeg", 0.86);
      setAvatarId("custom_device_photo");
      setAvatarSrc(optimized);
      setProfileNotice("Your photo is saved privately on this device only.");
    } catch (error) {
      console.error("Profile photo upload error:", error);
      setProfileNotice("We could not prepare that image. Please try another one.");
    } finally {
      if (customPhotoInputRef.current) customPhotoInputRef.current.value = "";
    }
  };

  const handleLogout = () => {
    disconnectWs();
    [
      "access_token",
      "token",
      "role",
      "student_id",
      "student_pk",
      "org_code",
      "student_access_token",
      "student_token",
      "display_name",
      caseySessionStorageKey,
      STUDENT_PORTAL_RETURN_KEY,
    ].forEach(removeBoth);
    navigate("/student-login");
  };

  const selectedDoctor = useMemo(() => {
    const did = Number(clean(selectedDoctorId));
    return onlineDoctors.find((d) => d.id === did) || null;
  }, [onlineDoctors, selectedDoctorId]);

  useEffect(() => {
    if (!userTriggeredDoctorSelectRef.current) return;
    if (!selectedDoctor) return;

    setDoctorProfileDoc(selectedDoctor);
    setDoctorProfileOpen(true);
    userTriggeredDoctorSelectRef.current = false;
  }, [selectedDoctor]);

  const liveStatusLabel =
    liveStatus === "accepted"
      ? "Accepted ✅"
      : liveStatus === "requested"
      ? `Requested (${liveMode.toUpperCase()}) — waiting…`
      : wsStatus === "connected"
      ? "Ready"
      : "Disconnected";

  const requestLive = (mode: InviteMode) => {
    setWsError("");

    const did = Number(clean(selectedDoctorId) || clean(typedDoctorId));
    if (!did) {
      setWsError("Select an advisor first.");
      return;
    }
    if (!clean(studentPk)) {
      setWsError("Missing student_pk (database id). Log in again so we can store it.");
      return;
    }

    wsRunRef.current = true;
    const ok = ensureWs("requestLive");
    if (!ok || !wsRef.current) return;

    const rid = makeRoomIdFromPk(studentPk, did);
    setRoomId(rid);
    setLiveMode(mode);
    setLiveStatus("requested");

    requestedModeRef.current = mode;
    setVideoOpen(false);

    setLiveMsgs([
      { id: makeClientMsgId(), from: "system", text: "Request sent. Waiting for an advisor to accept…", ts: Date.now() },
    ]);
    setLiveText("");

    const k = `student_chat_doctor:${normOrgCode(orgCodeRaw)}:${clean(studentId)}`;
    try {
      localStorage.setItem(k, String(did));
      sessionStorage.setItem(k, String(did));
    } catch {}

    const displayForDoctor = clean(displayName) || clean(studentId) || "Student";

    const payload = {
      type: "invite.create",
      to_doctor_id: String(did),
      from_student_pk: String(studentPk),
      room_id: rid,
      org_code: orgCode,
      student_id: clean(studentId),

      display_name: displayForDoctor,
      avatar_id: clean(avatarId),
      avatar_src: clean(avatarSrc),

      risk_level: clean(lastCaseyContext.risk_level),
      category: clean(lastCaseyContext.primary_category),
      trigger_level: clean(lastCaseyContext.trigger_level),
      escalation: !!lastCaseyContext.escalation,

      mode,
      invite_mode: mode,
      chat_or_video: mode,
      is_video: mode === "video",
    };

    try {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      } else {
        setWsError("Realtime is connecting… try again in a second.");
        scheduleReconnect("Invite attempted while connecting");
        setLiveStatus("idle");
      }
    } catch {
      setWsError("Failed to send request (socket closed).");
      setLiveStatus("idle");
    }
  };


  const handleAuraSos = async (
    source: "manual" | "automatic" = "manual",
    safetyContext?: { triggerLevel?: string; category?: string; originatingMessage?: string; forceNew?: boolean },
  ) => {
    if (sosBusyRef.current || liveStatus === "requested" || liveStatus === "accepted") return;
    sosBusyRef.current = true;
    setSosBusy(true);
    setSosStatus(source === "automatic"
      ? "AURA ERH™ detected an urgent safety concern. Connecting you to the first available live advisor…"
      : "AURA SOS™ activated. Connecting you to the first available live advisor…");
    setWsError("");
    setTab("live");

    try {
      if (!clean(studentPk)) {
        setSosStatus("We could not identify your student session. Please log in again, or call 988 or 911 if you are in immediate danger.");
        return;
      }

      // Ask the student's device for an exact location at the moment the ERH
      // incident is created. A denied or unavailable location never blocks the
      // emergency broadcast.
      const deviceLocation = await captureDeviceLocation();

      wsRunRef.current = true;
      ensureWs("aura-sos-broadcast");
      for (let i = 0; i < 24; i += 1) {
        if (wsRef.current?.readyState === WebSocket.OPEN) break;
        await sleep(250);
      }
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setSosStatus("The live connection is unavailable. Call or text 988, or call 911 if you are in immediate danger.");
        return;
      }

      const displayForDoctor = clean(displayName) || clean(studentId) || "Student";
      // The backend Incident Controller assigns the canonical room from the
      // single active incident. Do not generate a second room or incident here.
      setRoomId("");
      setLiveMode("chat");
      setLiveStatus("requested");
      requestedModeRef.current = "chat";
      setVideoOpen(false);
      const selectedAdvisorId = clean(selectedDoctorId);
      const selectedAdvisor = onlineDoctors.find((doctor) => String(doctor.id) === selectedAdvisorId);
      const selectedAdvisorName = clean(selectedAdvisor?.name) || "your selected wellness advisor";
      const broadcastFallback = !selectedAdvisorId;

      setLiveMsgs([{
        id: makeClientMsgId(),
        from: "system",
        text: broadcastFallback
          ? "AURA ERH™ is active. No selected advisor was available, so your emergency alert was sent to the online wellness advisor team."
          : `AURA ERH™ is active. Your emergency request was sent directly to ${selectedAdvisorName}.`,
        ts: Date.now(),
      }]);
      setLiveText("");

      wsRef.current.send(JSON.stringify({
        type: "invite.create",
        broadcast_to_advisors: broadcastFallback,
        to_doctor_id: selectedAdvisorId,
        from_student_pk: String(studentPk),
        org_code: orgCode,
        student_id: clean(studentId),
        display_name: displayForDoctor,
        avatar_id: clean(avatarId),
        avatar_src: clean(avatarSrc),
        session_id: caseySessionId,
        preferred_language: clean(translateLanguageRef.current) || "English",
        student_language: clean(translateLanguageRef.current) || "English",
        force_new_incident: false,
        mode: "chat", invite_mode: "chat", chat_or_video: "chat", is_video: false,
        aura_sos: true, emergency_help: true, emergency: true, high_priority: true, priority: "critical",
        source: source === "automatic" ? "casey_auto_safety_trigger" : "student_aura_sos",
        category: clean(safetyContext?.category) || clean(lastCaseyContext.primary_category) || "aura_sos",
        risk_level: "critical",
        trigger_level: clean(safetyContext?.triggerLevel) || clean(lastCaseyContext.trigger_level) || (source === "automatic" ? "automatic_high_risk" : "student_requested_sos"),
        escalation: true,
        latitude: deviceLocation.latitude ?? null,
        longitude: deviceLocation.longitude ?? null,
        location_accuracy: deviceLocation.accuracy ?? null,
        location_captured_at: deviceLocation.captured_at,
        location_permission: deviceLocation.permission_status,
        context: {
          location_source: deviceLocation.permission_status === "granted" ? "user_device" : deviceLocation.permission_status,
          latitude: deviceLocation.latitude ?? null,
          longitude: deviceLocation.longitude ?? null,
          accuracy: deviceLocation.accuracy ?? null,
          captured_at: deviceLocation.captured_at,
          originating_message: clean(safetyContext?.originatingMessage),
          automatic_trigger: source === "automatic",
          student_language: clean(translateLanguageRef.current) || "English",
          preferred_language: clean(translateLanguageRef.current) || "English",
        },
      }));

      setSosStatus(deviceLocation.permission_status === "granted" && deviceLocation.accuracy != null
        ? `AURA ERH™ is active. Exact device location captured (accuracy about ${Math.round(deviceLocation.accuracy)} m). Waiting for the first online advisor to accept.`
        : `AURA ERH™ is active. Waiting for the first online advisor to accept. Device location status: ${deviceLocation.permission_status}.`);
    } catch (error) {
      console.error("AURA SOS error:", error);
      setLiveStatus("idle");
      setSosStatus("AURA ERH™ could not complete the live connection. Call or text 988, or call 911 if you are in immediate danger.");
    } finally {
      sosBusyRef.current = false;
      setSosBusy(false);
    }
  };

  const useTypedId = () => {
    const did = Number(clean(typedDoctorId));
    if (!did) {
      setWsError("Enter a valid Advisor ID.");
      return;
    }
    userTriggeredDoctorSelectRef.current = true;
    setSelectedDoctorId(String(did));
    setWsError("");
  };

  const tipsBtnStyle: React.CSSProperties = {
    height: 40,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.18)",
    background: "linear-gradient(180deg, rgba(34,197,94,0.98), rgba(22,163,74,0.98))",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 10px 22px rgba(16, 185, 129, 0.35), 0 2px 0 rgba(0,0,0,0.10)",
    textShadow: "0 1px 0 rgba(0,0,0,0.18)",
  };

  const subtleBtn: React.CSSProperties = {
    height: 40,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.15)",
    background: "rgba(255,255,255,0.95)",
    cursor: "pointer",
    fontWeight: 900,
  };

  const goToTips = () => {
    const portalPath = getPortalReturn();
    setPortalReturn(portalPath);
    navigate("/student/tips", { state: { from: portalPath } });
  };

  return (
    <div className={`student-dashboard student-dashboard--${themePreference.toLowerCase()}`}>
      <div className="student-dashboard__shell">
        <header className="student-dashboard__header">
          <div className="student-brand">
            <AvatarImage
              src={drIsInLogo}
              alt="Doctor Is In"
              size={52}
              fit="cover"
              radius={16}
              background="#ffffff"
              border="1px solid rgba(15, 55, 82, 0.12)"
            />
            <div>
              <div className="student-brand__name">Doctor Is In</div>
              <div className="student-brand__tagline">AI Wellness Advisor with a human touch.</div>
            </div>
          </div>

          <div className="student-welcome" aria-live="polite">
            <div>Welcome back, <strong>{greetingName}</strong>! 👋</div>
            <span>Casey is here whenever you need someone to talk to.</span>
          </div>

          <div className="student-header-actions">
            <button type="button" className="student-btn student-btn--primary" onClick={goToTips}>
              <span aria-hidden="true">💡</span> Tips &amp; Resources
            </button>
            <button type="button" className="student-btn student-btn--ghost" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        <main className="student-dashboard__grid">
          <section className="student-card student-profile-card" aria-labelledby="student-profile-heading">
            <div className="student-section-heading">
              <span className="student-section-icon" aria-hidden="true">♙</span>
              <div>
                <h2 id="student-profile-heading">My Profile</h2>
                <p>Personalize your space. Casey will remember your preferences.</p>
              </div>
            </div>

            <div className="student-divider" />
            <div className="student-field-label">My Avatar</div>
            <div className="student-avatar-preview-grid">
              <button type="button" className="student-avatar-tile student-avatar-tile--selected" onClick={() => setGalleryOpen(true)}>
                <AvatarImage
                  src={avatarSrc || caseyImage}
                  alt="Selected student avatar"
                  width={82}
                  height={82}
                  fit="cover"
                  radius={18}
                  background="#eef8fb"
                  fallback={<span>{studentFallback}</span>}
                />
                <span className="student-avatar-check">✓</span>
              </button>
              {AVATARS.slice(1, 5).map((avatar) => (
                <button key={avatar.id} type="button" className="student-avatar-tile" onClick={() => onPickAvatar(avatar)}>
                  <img src={avatar.src} alt={avatar.label} />
                </button>
              ))}
              <button type="button" className="student-avatar-tile student-avatar-tile--more" onClick={() => setGalleryOpen(true)}>
                <span>＋</span>
                <small>View More</small>
              </button>
            </div>

            <div className="student-profile-actions">
              <button type="button" className="student-link-button" onClick={() => setGalleryOpen(true)}>Choose Avatar</button>
              <input
                ref={customPhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void handleCustomPhotoUpload(event.target.files?.[0])}
                hidden
              />
              <button type="button" className="student-link-button" onClick={() => customPhotoInputRef.current?.click()}>Upload Photo</button>
            </div>

            <label className="student-field-label" htmlFor="student-display-name">Display Name</label>
            <input
              id="student-display-name"
              className="student-control"
              value={displayName}
              maxLength={40}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Type any name you want"
            />

            <label className="student-field-label" htmlFor="student-about-me">About Me <span>(optional)</span></label>
            <div className="student-textarea-wrap">
              <textarea
                id="student-about-me"
                className="student-control student-textarea"
                value={aboutMe}
                maxLength={120}
                onChange={(event) => setAboutMe(event.target.value)}
                placeholder="Share a little about yourself..."
              />
              <small>{aboutMe.length}/120</small>
            </div>

            <div className="student-field-label student-preferences-label">My Preferences</div>
            <div className="student-preferences">
              <button
                type="button"
                onClick={() => {
                  const nextLanguage = translateLanguage === "English" ? "Spanish" : "English";
                  handleLanguageChange(nextLanguage);
                }}
              >
                <span>◉ Language</span><strong>{translateLanguage}</strong>
              </button>
              <button type="button" onClick={() => setThemePreference((value) => value === "Light" ? "Calm" : "Light")}>
                <span>✦ Theme</span><strong>{themePreference}</strong>
              </button>
              <button type="button" onClick={() => setRemindersEnabled((value) => !value)}>
                <span>⏰ Reminders</span><strong>{remindersEnabled ? "On" : "Off"}</strong>
              </button>
              <button type="button" onClick={() => setSoundEnabled((value) => !value)}>
                <span>🔊 Sound</span><strong>{soundEnabled ? "On" : "Off"}</strong>
              </button>
            </div>

            {profileNotice ? <div className="student-profile-notice">{profileNotice}</div> : null}
            <button type="button" className="student-btn student-btn--primary student-save-profile" onClick={() => setProfileNotice("Your profile preferences are saved on this device.")}>
              Save My Profile
            </button>
          </section>

          <section className="student-center-column">
            <div className="student-card student-chat-card">
              <div className="student-tabs" role="tablist" aria-label="Student support options">
                <button type="button" role="tab" aria-selected={tab === "ai"} className={tab === "ai" ? "active" : ""} onClick={() => setTab("ai")}>
                  💬 AI Advisor Chat
                </button>
                <button type="button" role="tab" aria-selected={tab === "live"} className={tab === "live" ? "active" : ""} onClick={() => setTab("live")}>
                  🎧 Talk to a Live Advisor
                </button>
              </div>

              {tab === "ai" ? (
                <>
                  <div className="student-chat-toolbar">
                    <h2>Chat with Casey</h2>
                    <div>
                      <select
                        value={translateLanguage}
                        onChange={(event) => handleLanguageChange(event.target.value)}
                        aria-label="Chat language"
                        dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                      >
                        {LANGUAGE_OPTIONS.map((language) => (
                          <option
                            key={language.code}
                            value={language.value}
                            dir={language.rtl ? "rtl" : "ltr"}
                          >
                            {language.label}
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={handleNewCaseyChat} disabled={aiBusy}>💬 New Chat</button>
                    </div>
                  </div>

                  {systemNotice ? <div className="student-system-notice">{systemNotice}</div> : null}

                  <div className="student-chat-thread" ref={aiScrollBoxRef}>
                    {aiMessages.length === 0 ? (
                      <div className="student-casey-welcome">
                        <AvatarCircle src={caseyImage} fallback="C" alt="Casey" />
                        <div>
                          <h3>👋 Hi {greetingName}!</h3>
                          <p>I’m Casey. I’m here to listen without judgment and support your health and wellness.</p>
                          <p>What’s on your mind today?</p>
                        </div>
                      </div>
                    ) : (
                      <div className="student-message-list">
                        {aiMessages.map((message) => {
                          const studentMessage = message.from === "student";
                          return (
                            <div key={message.id} className={`student-message-row ${studentMessage ? "student" : "casey"}`}>
                              {!studentMessage ? <AvatarCircle src={caseyImage} fallback="C" alt="Casey" /> : null}
                              <div className="student-message-bubble">{message.text}</div>
                              {studentMessage ? <AvatarCircle src={avatarSrc || ""} fallback={studentFallback} alt="Student" /> : null}
                            </div>
                          );
                        })}
                        <div ref={aiEndRef} />
                      </div>
                    )}
                  </div>

                  <div className="student-quick-prompts" aria-label="Conversation starters">
                    {["😟 I’m feeling stressed", "💬 I need advice", "🌧️ Having a tough day", "💗 Something’s bothering me", "👋 Just want to talk"].map((prompt) => (
                      <button key={prompt} type="button" onClick={() => setAiText(prompt.replace(/^[^ ]+ /, ""))}>{prompt}</button>
                    ))}
                  </div>

                  <div className="student-composer">
                    <span aria-hidden="true">📎</span>
                    <input
                      value={aiText}
                      disabled={aiBusy}
                      onChange={(event) => setAiText(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          void sendAi();
                        }
                      }}
                      placeholder="Type your message here..."
                      dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                    />
                    <small>{aiText.length}/1000</small>
                    <button type="button" onClick={() => void sendAi()} disabled={aiBusy} aria-label="Send message">➤</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="student-chat-toolbar">
                    <h2>Talk to a Live Advisor</h2>
                    <span className={`student-status-pill ${liveStatus}`}>{liveStatusLabel}</span>
                  </div>

                  <div className="student-advisor-picker">
                    <select value={selectedDoctorId} onChange={(event) => { userTriggeredDoctorSelectRef.current = true; setSelectedDoctorId(event.target.value); }}>
                      <option value="">Choose an available advisor</option>
                      {onlineDoctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>{doctor.name}{doctor.is_online ? " • Online" : ""}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => requestLive("chat")}>Request Chat</button>
                    <button type="button" onClick={() => requestLive("video")}>Request Video</button>
                  </div>

                  {wsError ? <div className="student-system-notice student-system-notice--error">{wsError}</div> : null}
                  <div className="student-chat-thread" ref={liveScrollBoxRef}>
                    {liveStatus === "idle" ? (
                      <div className="student-live-empty">
                        <strong>Live advisors are here when you need a person.</strong>
                        <span>Choose an available advisor and request a private chat or video session.</span>
                      </div>
                    ) : (
                      <div className="student-message-list">
                        {liveMsgs.map((message) => {
                          const studentMessage = message.from === "student";
                          return (
                            <div key={message.id} className={`student-message-row ${studentMessage ? "student" : "casey"}`}>
                              {!studentMessage ? <AvatarCircle src={selectedDoctor?.photo_url || ""} fallback="A" alt="Advisor" /> : null}
                              <div className="student-message-bubble">{message.text}</div>
                              {studentMessage ? <AvatarCircle src={avatarSrc || ""} fallback={studentFallback} alt="Student" /> : null}
                            </div>
                          );
                        })}
                        <div ref={liveEndRef} />
                      </div>
                    )}
                  </div>
                  {liveStatus === "accepted" ? (
                    <div className="student-composer">
                      <input
                        value={liveText}
                        onChange={(event) => setLiveText(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            sendLiveChat();
                          }
                        }}
                        placeholder="Type your private message..."
                        dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                      />
                      <button type="button" onClick={sendLiveChat} aria-label="Send message">➤</button>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="student-card student-support-card">
              <h3>How Casey can help</h3>
              <div className="student-support-features">
                <div><span>🔔</span><strong>Listen &amp; Support</strong><small>Talk about what’s on your mind</small></div>
                <div><span>🤝</span><strong>Health &amp; Wellness</strong><small>Get trusted info on health topics</small></div>
                <div><span>📋</span><strong>Resources</strong><small>Find helpful tools and support</small></div>
                <div><span>♡</span><strong>Crisis Support</strong><small>We’re here for you 24/7</small></div>
              </div>
              <div className="student-connected-strip">
                <h3>You’re Connected &amp; Supported</h3>
                <div>
                  <span><b>🛡 Realtime Status</b><small>{wsStatus === "connected" ? "Connected •" : "Connecting..."}</small></span>
                  <span><b>👥 Advisor Roster</b><small>{onlineDoctors.some((doctor) => doctor.is_online) ? "Online •" : "Available"}</small></span>
                  <span><b>◷ Response Time</b><small>Usually within seconds</small></span>
                  <span><b>🔒 Your Privacy</b><small>Always protected •</small></span>
                </div>
              </div>
            </div>
          </section>

          <aside className="student-card student-emergency-card" aria-label="Emergency Help">
            <h2>🛡 EMERGENCY HELP</h2>
            <button type="button" className="student-sos-button" onClick={() => void handleAuraSos("manual")} disabled={sosBusy} aria-label="Activate AURA SOS emergency help">
              <span>AURA</span><strong>SOS<sup>™</sup></strong>
            </button>
            <h3>GET HELP NOW</h3>
            <p>Press the <strong>AURA SOS™</strong> button anytime you need immediate help. You will be connected to a live wellness advisor right away.</p>

            {sosStatus ? <div className="student-sos-status">{sosStatus}</div> : null}

            <div className="student-emergency-benefits">
              <div><span>🛡</span><p><strong>Confidential</strong><small>Your privacy is always protected</small></p></div>
              <div><span>◷</span><p><strong>Immediate Support</strong><small>We’re here 24/7</small></p></div>
              <div><span>♙</span><p><strong>Trained Advisors</strong><small>Caring professionals ready to help</small></p></div>
              <div><span>✦</span><p><strong>Safe &amp; Secure</strong><small>Your safety is our priority</small></p></div>
            </div>

            <div className="student-emergency-callout">📞 If you are in immediate danger,<br /><strong>call 988 or 911 now.</strong></div>
          </aside>
        </main>

        <footer className="student-dashboard__footer">
          <div>© {year} Doctor Is In App.<br /><span>All rights reserved.</span></div>
          <div className="student-footer-message">🛡 <strong>Your well-being is important.<br />We’re here whenever you need us.</strong></div>
          <div><span>Privacy Policy &nbsp; | &nbsp; Terms of Use &nbsp; | &nbsp; Contact Us</span><br />Patent Pending {year}</div>
        </footer>
      </div>

      <DoctorProfileModal open={doctorProfileOpen} doctor={doctorProfileDoc} onClose={() => setDoctorProfileOpen(false)} />

      {videoOpen && roomId ? (
        <div className="student-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setVideoOpen(false)}>
          <div className="student-video-modal" onClick={(event) => event.stopPropagation()}>
            {wsRef.current && wsRef.current.readyState === WebSocket.OPEN ? (
              <VideoRoom ws={wsRef.current} roomId={roomId} role="student" onHangup={() => setVideoOpen(false)} />
            ) : <div className="student-system-notice student-system-notice--error">Realtime socket not connected.</div>}
          </div>
        </div>
      ) : null}

      <AvatarGalleryModal
        open={galleryOpen}
        title="Choose an avatar"
        avatars={AVATARS}
        selectedId={avatarId}
        onSelect={onPickAvatar}
        onClose={() => setGalleryOpen(false)}
        onClear={clearAvatar}
      />
    </div>
  );
}